import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, UserPlus, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import API_BASE_URL from '@/config';
import { PrescriptionMedicineSection } from '@/components/prescriptions/PrescriptionMedicineSection';

export const AddPrescription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const attachedImage = location.state?.attachedImage;
  const initialPatientId = location.state?.patientId ? String(location.state.patientId) : '';
  const todayStr = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    patientId: initialPatientId,
    visitDate: todayStr,
    followUpDate: '',
    diet: '',
    lifestyle: '',
    notes: ''
  });


  const [patients, setPatients] = useState([]);
  const [medicines, setMedicines] = useState([
    { name: '', dosage: '', frequency: '', duration: '', erp_product_id: null, dispensed_quantity: 1, dispensed_unit: 'UNIT' }
  ]);
  const [saving, setSaving] = useState(false);

  // Create Patient Modal State (matches /admin/patients/new)
  const [createPatientModalOpen, setCreatePatientModalOpen] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '',
    email: '',
    address: ''
  });
  const [savingPatient, setSavingPatient] = useState(false);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/patients`);
        setPatients(response.data?.value || response.data || []);
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
    };

    fetchPatients();
  }, []);

  const handleAddMedicine = () => {
    setMedicines([...medicines, { name: '', dosage: '', frequency: '', duration: '' }]);
  };

  const handleRemoveMedicine = (index) => {
    const newMedicines = medicines.filter((_, i) => i !== index);
    setMedicines(newMedicines);
  };

  const handleMedicineChange = (index, field, value) => {
    const newMedicines = [...medicines];
    newMedicines[index][field] = value;
    setMedicines(newMedicines);
  };

  const handleCreatePatientSubmit = async (e) => {
    e.preventDefault();
    if (!newPatientData.name.trim() || !newPatientData.age || !newPatientData.gender) {
      toast.error('Please fill in required fields: Full Name, Age, and Gender');
      return;
    }

    try {
      setSavingPatient(true);
      const response = await axios.post(`${API_BASE_URL}/api/patients`, {
        name: newPatientData.name.trim(),
        age: parseInt(newPatientData.age, 10),
        gender: newPatientData.gender,
        phone: newPatientData.phone.trim(),
        email: newPatientData.email.trim(),
        address: newPatientData.address.trim()
      });

      const createdPatient = response.data;
      toast.success('Patient added successfully!');

      // Add to patient dropdown list and auto-select
      setPatients(prev => [createdPatient, ...prev]);
      setFormData(prev => ({ ...prev, patientId: String(createdPatient.id) }));

      // Reset modal fields and close
      setNewPatientData({
        name: '',
        age: '',
        gender: '',
        phone: '',
        email: '',
        address: ''
      });
      setCreatePatientModalOpen(false);
    } catch (error) {
      console.error('Error creating patient:', error);
      toast.error(error.response?.data?.message || 'Failed to save patient. Please try again.');
    } finally {
      setSavingPatient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Find the selected patient to get their name (IDs are stored as numbers on the server)
    const selectedPatient = patients.find(p => p.id === Number(formData.patientId));
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        patient_id: formData.patientId,
        patient_name: selectedPatient.name,
        visit_date: formData.visitDate,
        follow_up_date: formData.followUpDate || '',
        medicines: medicines
          .filter((m) => m && m.name && m.name.trim())
          .map((m) => ({
            name: m.name.trim(),
            dosage: m.dosage || '',
            frequency: m.frequency || '',
            duration: m.duration || '',
            erp_product_id: m.erp_product_id ? Number(m.erp_product_id) : null,
            dispensed_quantity:
              m.dispensed_quantity !== undefined && m.dispensed_quantity !== ''
                ? Number(m.dispensed_quantity)
                : 1.0,
            dispensed_unit: m.dispensed_unit || 'UNIT'
          })),
        diet: formData.diet || '',
        lifestyle: formData.lifestyle || '',
        notes: formData.notes || '',
        status: 'Active',
        type: attachedImage ? 'Uploaded' : 'Digital'
      };

      if (attachedImage) {
        payload.file_url = attachedImage.file_url;
        payload.cloudinary_id = attachedImage.cloudinary_id;
        payload.file_name = attachedImage.file_name;
        payload.file_type = attachedImage.file_type;
      }

      await axios.post(`${API_BASE_URL}/api/prescriptions`, payload);
      // Update patient's last visit so UI shows correct last visit
      try {
        await axios.post(`${API_BASE_URL}/api/patients/${formData.patientId}/last_visit`, { last_visit: payload.visit_date });
      } catch (err) {
        console.warn('Failed to update patient last_visit:', err);
      }
      toast.success('Prescription created successfully!');
      navigate('/admin/prescriptions');
    } catch (error) {
      console.error('Error creating prescription:', error);
      const errMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to create prescription';
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/prescriptions')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-serif text-3xl font-bold">Create Digital Prescription</h1>
          <p className="text-muted-foreground">Fill in the prescription details below</p>
        </div>
      </div>

      {attachedImage && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-in fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                Prescription Photo Attached
              </p>
              <p className="text-xs text-muted-foreground">
                {attachedImage.file_name || 'Prescription scan'}
              </p>
            </div>
          </div>
          {attachedImage.file_url && (
            <a
              href={attachedImage.file_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
            >
              View Uploaded File ↗
            </a>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Basic Information */}
        <Card className="shadow-card">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-xl">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="patient">Select Patient *</Label>
                <Select
                  value={formData.patientId}
                  onValueChange={(value) => {
                    if (value) {
                      setFormData(prev => ({ ...prev, patientId: value }));
                    }
                  }}
                  required
                >
                  <SelectTrigger id="patient">
                    <SelectValue placeholder="Choose patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(patient => (
                      <SelectItem key={patient.id} value={patient.id.toString()}>
                        {patient.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="pt-0.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1.5 text-primary border-primary/30 hover:bg-primary/5 hover:text-primary w-full sm:w-auto"
                    onClick={() => setCreatePatientModalOpen(true)}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Create New Patient
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="visitDate">Visit Date *</Label>
                <Input
                  id="visitDate"
                  type="date"
                  value={formData.visitDate}
                  onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="followUpDate">Follow-up Date</Label>
                <Input
                  id="followUpDate"
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Diet & Lifestyle */}
        <Card className="shadow-card">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-xl">Diet & Lifestyle (Ahara & Vihara)</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="diet">Dietary Recommendations (Ahara)</Label>
                <Textarea
                  id="diet"
                  placeholder="Enter dietary guidelines, foods to include/avoid..."
                  className="min-h-[100px]"
                  value={formData.diet}
                  onChange={(e) => setFormData({ ...formData, diet: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lifestyle">Yoga & Lifestyle (Vihara)</Label>
                <Textarea
                  id="lifestyle"
                  placeholder="Enter yoga postures, lifestyle modifications, daily routine..."
                  className="min-h-[100px]"
                  value={formData.lifestyle}
                  onChange={(e) => setFormData({ ...formData, lifestyle: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Medicines */}
        <PrescriptionMedicineSection medicines={medicines} onChange={setMedicines} />

        {/* Additional Notes */}
        <Card className="shadow-card">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-xl">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Textarea
              placeholder="Any additional instructions or notes..."
              className="min-h-[100px]"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto h-11"
            onClick={() => navigate('/admin/prescriptions')}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={saving} className="w-full sm:w-auto h-11">
            Save Prescription
          </Button>
        </div>
      </form>

      {/* Create New Patient Modal - identical structure to /admin/patients/new */}
      <Dialog open={createPatientModalOpen} onOpenChange={setCreatePatientModalOpen}>
        <DialogContent className="sm:max-w-xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl sm:text-2xl font-bold flex items-center gap-2">
              <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              Add New Patient
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs sm:text-sm">
              Enter patient information below. Once created, this patient will be automatically selected for this prescription.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreatePatientSubmit} className="space-y-4 sm:space-y-6 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="new-patient-name" className="text-sm">Full Name *</Label>
                <Input
                  id="new-patient-name"
                  type="text"
                  placeholder="Enter full name"
                  className="h-11"
                  value={newPatientData.name}
                  onChange={(e) => setNewPatientData({ ...newPatientData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-patient-age" className="text-sm">Age *</Label>
                <Input
                  id="new-patient-age"
                  type="number"
                  placeholder="Enter age"
                  className="h-11"
                  value={newPatientData.age}
                  onChange={(e) => setNewPatientData({ ...newPatientData, age: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-patient-gender" className="text-sm">Gender *</Label>
                <Select
                  value={newPatientData.gender}
                  onValueChange={(value) => setNewPatientData({ ...newPatientData, gender: value })}
                  required
                >
                  <SelectTrigger id="new-patient-gender" className="h-11">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-patient-phone" className="text-sm">Phone Number</Label>
                <Input
                  id="new-patient-phone"
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  className="h-11"
                  value={newPatientData.phone}
                  onChange={(e) => setNewPatientData({ ...newPatientData, phone: e.target.value })}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="new-patient-email" className="text-sm">Email</Label>
                <Input
                  id="new-patient-email"
                  type="email"
                  placeholder="email@example.com"
                  className="h-11"
                  value={newPatientData.email}
                  onChange={(e) => setNewPatientData({ ...newPatientData, email: e.target.value })}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="new-patient-address" className="text-sm">Address</Label>
                <Input
                  id="new-patient-address"
                  type="text"
                  placeholder="Enter address"
                  className="h-11"
                  value={newPatientData.address}
                  onChange={(e) => setNewPatientData({ ...newPatientData, address: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 flex flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto h-11"
                onClick={() => setCreatePatientModalOpen(false)}
                disabled={savingPatient}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={savingPatient} className="w-full sm:w-auto h-11">
                Add Patient
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddPrescription;
